/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
};

function SearchCustomer({ callback }) {
  const mod = 'tax_invoice';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalOpen, setModalOpen] = useState(false);
  const txtSearch = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.code', label: t(`attr.code`), align: 'left' },
    { id: 'tb.name', label: 'ลูกค้า', align: 'left' },
    { id: 'tb.taxid', label: 'เลขผู้เสียภาษี', align: 'left' },
    { id: 'tb.branch', label: 'สาขา', align: 'left' },
  ];

  const view = (data) => {
    setFind(data);
    closeShow();

    FetchData('OPTIONS', `/${mod}/customer`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
    });
  };

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const pick = (item) => {
    const parts = [
      item.address,
      item.subdistrict_name,
      item.district_name,
      item.province_name,
      item.zipcode,
    ]
      .filter(Boolean)
      .join(' ');
    setModalOpen(false);
    callback({
      customer_id: item.id,
      customer_name: item.name,
      customer_address: parts,
      customer_tax_id: item.taxid || '',
      customer_branch_code: '',
      customer_branch_name: item.branch || '',
    });
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  return (
    <div>
      <MDButton
        variant='outlined'
        tabIndex={-1}
        color='dark'
        size='small'
        iconOnly
        onClick={() => {
          setModalOpen(true);
          view(find);
          setTimeout(() => txtSearch.current?.focus(), 500);
        }}
      >
        <Icon>search</Icon>
      </MDButton>

      <Dialog
        open={modalOpen}
        maxWidth='xl'
        keepMounted
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          เลือกลูกค้า
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDBox
                  noValidate
                  component='form'
                  role='form'
                  onSubmit={(e) => {
                    e.preventDefault();
                    view({ ...find, page: 1 });
                  }}
                >
                  <MDInput
                    name='searchform'
                    fullWidth
                    value={find.search}
                    type='search'
                    onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                    InputProps={{
                      inputRef: txtSearch,
                      startAdornment: (
                        <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                      ),
                      autoComplete: 'off',
                    }}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <TableContainer sx={{ boxShadow: 0, minHeight: 400 }}>
                  <Table>
                    <TableHeader
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleSort}
                      data={initHeader}
                    />
                    <TableBody>
                      {record.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell align='left'>
                            <MDButton
                              variant='text'
                              color='info'
                              size='small'
                              sx={{ fontSize: '1rem' }}
                              onClick={() => pick(row)}
                            >
                              {row.code}
                            </MDButton>
                          </TableCell>
                          <TableCell align='left'>{row.name}</TableCell>
                          <TableCell align='left'>{row.taxid || '-'}</TableCell>
                          <TableCell align='left'>{row.branch || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                <MDBox display='flex' justifyContent='space-between'>
                  <MDButton
                    variant={show ? 'contained' : 'outlined'}
                    color='dark'
                    onClick={openShow}
                  >
                    {t(`element.show_record`, { num: find.perPage })}&nbsp;
                    <Icon>keyboard_arrow_down</Icon>
                  </MDButton>
                  {renderShow}
                  <Pagination
                    count={pageTotal}
                    variant='outlined'
                    color='secondary'
                    showFirstButton
                    showLastButton
                    page={pageNo}
                    onChange={(e, p) => view({ ...find, page: p })}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
      </Dialog>
    </div>
  );
}

SearchCustomer.propTypes = {
  callback: PropTypes.func.isRequired,
};

export default SearchCustomer;
